import React, { useEffect } from 'react'
import { Col, Row, Form, FormGroup } from 'reactstrap'
import Joi from 'joi'
import 'devextreme/dist/css/dx.common.css'
import 'devextreme/dist/css/dx.light.css'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  createDataset,
  setEntityError,
} from '../../../redux/slices'
import ICSTextInput from '../../../components/ICSTextInput/ICSTextInput'
import ICSTextAreaInput from '../../../components/ICSTextAreaInput/ICSTextAreaInput'
import { useForm } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'
import ComponentBoxFooter from '../../../components/ComponentBox/ComponentBoxFooter/ComponentBoxFooter'
import ICSButton from '../../../components/ICS/Button/ICSButton'
import { ScrollView } from 'devextreme-react'
import ICSErrorCard from '../../../components/ICSErrorCard/ICSErrorCard'
import { hasInvalidField, invalidFieldsMessage } from '../../../utils/helpers'
import { AppDispatch } from '../../../services/lib'
import { DefaultState } from '../../../types'
import { DataSetBody, IpaDatasetFilter } from '../types'
import { RequestErrorResponse } from '../../../services/lib/types'

const defaultJoiSchema = {
  name: Joi.string().required().min(3).max(30).messages({
    'string.empty': `Required field`,
    'string.min': `Must be at lease {#limit} in length`,
  }),
  description: Joi.string().min(0).max(255).allow(''),
}

const schema = Joi.object(defaultJoiSchema)

export const DataSetForm = ({
  filters,
  hideModal,
  selectedTotal,
  location,
}): JSX.Element => {
  const isEdit = location?.state && location?.state?.isEdit
  const selectedDataSet = isEdit ? location.state.dataset : null
  const datasetErrors = useSelector(
    (state: DefaultState) => state.datasets.error
  )

  const {
    formState: { errors, isSubmitted, isValid },
    handleSubmit,
    control,
    getValues,
    setError,
  } = useForm({
    mode: 'onChange',
    resolver: joiResolver(schema),
  })

  useEffect(() => {
    if (datasetErrors) {
      datasetErrors.fields.forEach(async errorObj =>
        setError(errorObj.field, {
          type: 'manual',
          message: errorObj.message,
        })
      )
    }
  }, [datasetErrors, setError])

  const dispatch: AppDispatch = useDispatch()
  const navigate = useNavigate()

  const generate = async () => {
    const { name, description } = getValues()

    const filterList: IpaDatasetFilter[] = []

    if (filters?.fleetId?.length > 0) {
      filterList.push({
        type: 'Fleet',
        value: filters.fleetId,
      })
    }

    if (filters?.vehicleId?.length > 0) {
      filterList.push({
        type: 'Vehicle',
        value: filters.vehicleId,
      })
    }

    if (filters?.names?.length > 0) {
      filterList.push({
        type: 'Collection',
        value: filters.names,
      })
    }

    if (filters?.extensions?.length > 0) {
      filterList.push({
        type: 'Extension',
        value: filters.extensions,
      })
    }

    if (filters?.startDate) {
      filterList.push({
        type: 'StartDate',
        value: new Date(filters.startDate + 'GMT'),
      })
    }

    if (filters?.endDate) {
      filterList.push({
        type: 'EndDate',
        value: new Date(filters.endDate),
      })
    }

    const body: DataSetBody = {
      name,
      description,
      type: 'dynamic',
      filters: filterList,
      upload_file_version_id: [],
    }

    if (!isEdit) {
      const createdDataset = await dispatch(createDataset(body))

      if (createdDataset.type.split('/').pop() === 'fulfilled') {
        hideModal()
        navigate('/ui2/datasets')
      } else if (createdDataset.type.split('/').pop() === 'rejected') {
        const verifyFields = await hasInvalidField(
          Object.keys(defaultJoiSchema),
          (createdDataset.payload as RequestErrorResponse).fields.map(
            fieldObj => fieldObj.field
          )
        )

        if (verifyFields.isInvalid) {
          dispatch(
            setEntityError({
              sliceName: 'datasets',
              entityName: 'Dataset',
              error: {
                code: 'error',
                message: invalidFieldsMessage,
                fields: verifyFields.fields,
              },
            })
          )
        }
      }
    }
  }

  return (
    <>
      <Row>
        <Col xs={{ size: 12 }}>
          {datasetErrors?.code === 'error' ? (
            <ICSErrorCard
              code={datasetErrors.code}
              message={datasetErrors.message}
              fields={datasetErrors.fields}
            />
          ) : (
            <ScrollView width='100%' height='100%'>
              <Form onSubmit={handleSubmit(generate)}>
                <FormGroup>
                  <Row>
                    <Col>
                      Creating data set with <b>{selectedTotal.count}</b> files,{' '}
                      <b>{selectedTotal.size}</b>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <ICSTextInput
                        name='name'
                        control={control}
                        errors={errors}
                        isSubmitted={isSubmitted}
                        defaultValue={isEdit ? selectedDataSet?.name : ''}
                      />
                      <ICSTextAreaInput
                        name='description'
                        control={control}
                        errors={errors}
                        isSubmitted={isSubmitted}
                        defaultValue={
                          isEdit ? selectedDataSet?.description : ''
                        }
                      />
                    </Col>
                  </Row>
                </FormGroup>
              </Form>
            </ScrollView>
          )}
        </Col>
      </Row>
      <span>
        <ComponentBoxFooter>
          <ICSButton
            id='formSubmit'
            width={60}
            useSubmitBehavior={true}
            type='success'
            disabled={!isValid}
            onClick={handleSubmit(generate)}
          >
            Save
          </ICSButton>
        </ComponentBoxFooter>
      </span>
    </>
  )
}
